import { useRouter } from '@tanstack/react-router';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';
import { generatePath } from '@/services/router/generate-path';

const REPORTING_WEBHOOKS_ROUTE = '/workspaces/$workspaceId/integrations/reporting-webhooks';
const REPORTING_WEBHOOKS_NEW_ROUTE = '/workspaces/$workspaceId/integrations/reporting-webhooks/new';
// Ember's edit URL is a bare id, not `/:id/edit`. Kept identical so existing links and bookmarks
// resolve after the flip.
const REPORTING_WEBHOOKS_EDIT_ROUTE = '/workspaces/$workspaceId/integrations/reporting-webhooks/$webhookId';

/** The migrated directory index, which redirects on to the Connections tab. */
const INTEGRATIONS_INDEX_PATH = '/workspaces/:workspaceId/integrations';

export const REPORTING_WEBHOOKS_DOCS_URL = 'https://docs.customer.io/webhooks';

export type ReportingWebhookLinks = {
  newHref: string;
  editHref: (id: string) => string;
};

export function useReportingWebhookLinks(workspaceId: string): ReportingWebhookLinks {
  const router = useRouter();

  return {
    newHref: router.buildLocation({ to: REPORTING_WEBHOOKS_NEW_ROUTE, params: { workspaceId } }).href,
    editHref: (id: string) =>
      router.buildLocation({ to: REPORTING_WEBHOOKS_EDIT_ROUTE, params: { workspaceId, webhookId: id } }).href,
  };
}

/** The list href — where Cancel goes, and where a save returns to. */
export function useReportingWebhooksIndexHref(workspaceId: string): string {
  const router = useRouter();
  return router.buildLocation({ to: REPORTING_WEBHOOKS_ROUTE, params: { workspaceId } }).href;
}

export function useReportingWebhookBreadcrumbs(workspaceId: string): void {
  useRegisterBreadcrumbs(useReportingWebhookTrail(workspaceId));
}

function useReportingWebhookTrail(workspaceId: string) {
  const indexHref = useReportingWebhooksIndexHref(workspaceId);

  return [
    {
      id: 'integrations',
      text: 'Integrations',
      href: generatePath(INTEGRATIONS_INDEX_PATH, { workspaceId }),
    },
    {
      id: 'integrations_reporting_webhooks',
      text: 'Reporting webhooks',
      href: indexHref,
    },
  ];
}

export function useNewReportingWebhookBreadcrumbs(workspaceId: string): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    ...useReportingWebhookTrail(workspaceId),
    {
      id: 'integrations_reporting_webhooks_new',
      text: 'Add Webhook',
      href: router.buildLocation({ to: REPORTING_WEBHOOKS_NEW_ROUTE, params: { workspaceId } }).href,
    },
  ]);
}

export function useEditReportingWebhookBreadcrumbs(workspaceId: string, webhookId: string): void {
  const router = useRouter();

  useRegisterBreadcrumbs([
    ...useReportingWebhookTrail(workspaceId),
    {
      id: 'integrations_reporting_webhooks_edit',
      text: 'Edit Webhook',
      href: router.buildLocation({ to: REPORTING_WEBHOOKS_EDIT_ROUTE, params: { workspaceId, webhookId } }).href,
    },
  ]);
}
